import { memo, type ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectedItemsSelector } from '@air/redux-selected-items';

import {
  AgentSelectionBar,
  type AgentSelectionBarViewModel,
} from '~/components/v2/components/private-selection-bar/agent-selection-bar/AgentSelectionBar';
import { usePrivateSelectionBarActions } from '~/components/v2/components/private-selection-bar/hooks/usePrivateSelectionBarActions';
import { useSelectionBarMeasuredLayoutTier } from '~/components/v2/components/private-selection-bar/hooks/useSelectionBarMeasuredLayoutTier';
import { useSelectionBarSelectAll } from '~/components/v2/components/private-selection-bar/hooks/useSelectionBarSelectAll';
import { useSelectionBarVisibility } from '~/components/v2/components/private-selection-bar/hooks/useSelectionBarVisibility';
import { useSelectedItemsSummary } from '~/components/v2/components/shared/selectionBar/hooks/useSelectedItemsSummary';
import { getSelectionActionBarItemCountState } from '~/components/v2/components/shared/selectionBar/selectionBarItemCountState';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useFrozenWhile } from '~/hooks/useFrozenWhile';
import { usePrivateSelectAllTotalCount } from '~/hooks/usePrivateSelectAllTotalCount';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { hasOnlyCanvasesSelectedSelector } from '~/store/selectedItems/selectors';

const getSelectAllLabel = (selectAllTotalCount?: number | null) =>
  selectAllTotalCount ? `Select all ${selectAllTotalCount.toLocaleString()} items` : 'Select all items';

export type PrivateAgentSelectionBarProps = {
  /**
   * The shell is still drawing the bar — keep the last selection visible until its slide-out lands.
   * Omitted when the bar is mounted alone (e.g. component tests).
   */
  holdViewModelWhileCollapsed?: boolean;
  /** Sits in the Download/Share cluster (same 8px gap). */
  endCta?: ReactNode;
};

/**
 * The private selection bar as the agent space renders it: inline, with the shell owning the
 * portal, the placement and the surface.
 *
 * Every read of the selection happens here so there is one place to hold still:
 *
 * - Clearing a selection empties the store immediately, but the shell then spends the better part
 *   of half a second sliding out — so once the store is empty this keeps handing
 *   the view the last state it drew, and the chrome slides out instead of vanishing mid-animation.
 * - A replace click clears `selectedItems` on mousedown and only commits the new item on mouseup.
 *   `useSelectionBarVisibility` keeps the shell open for that window; this keeps drawing the
 *   previous selection until the commit lands.
 *
 * Holding still is decided here rather than passed in, because whoever is animating this out may
 * well be rendering a cached copy of it by then, and would be handing it a stale answer.
 *
 * No canvas-only clearing effects here — `PrivateWorkspaceAgentSpace` is mounted by the layout and
 * outlives navigation, so it watches the pathname rather than relying on an unmount.
 */
export const PrivateAgentSelectionBar = memo(
  ({ endCta, holdViewModelWhileCollapsed = false }: PrivateAgentSelectionBarProps) => {
    const selectedItems = useSelector(selectedItemsSelector);
    const hasOnlyCanvasesSelected = useSelector(hasOnlyCanvasesSelectedSelector);
    const selectAllTotalCount = usePrivateSelectAllTotalCount();
    const { collectionsEnabled } = useFeatureFlags();
    const { generativeCanvasEnabled, selectAllEnabled } = useTemporaryFeatureFlags();
    const isRightPanelOpen = useWorkspaceRightPanelOpen();
    const isSplitView = generativeCanvasEnabled && isRightPanelOpen;
    const selectAllEnabledForView = selectAllEnabled && !hasOnlyCanvasesSelected;

    const selectAllLabel = getSelectAllLabel(selectAllTotalCount);
    const hasShowcaseShare = collectionsEnabled && !hasOnlyCanvasesSelected && selectedItems.length > 1 && !isSplitView;
    const omitBoardName = isRightPanelOpen;
    const { isSearchActive } = useIsSearchActive();
    const { countLabel, scopeLabel } = useSelectedItemsSummary({ selectAllTotalCount, omitBoardName });

    const hasScopeLabel = !omitBoardName && !!scopeLabel;

    const { hasSelection: visibilityHasSelection, isPendingCommit } = useSelectionBarVisibility();
    const shouldFreezeViewModel = isPendingCommit || (holdViewModelWhileCollapsed && !visibilityHasSelection);

    const {
      onClearAll,
      toggleSelectAll,
      enableIncludeNestedItems,
      isSelectAllActive: isSelectAllActiveFromHook,
      canSelectAllInCurrentView,
      canOfferSelectAll,
      canIncludeNestedItems,
    } = useSelectionBarSelectAll({ selectAllEnabled: selectAllEnabledForView, selectAllTotalCount });

    const willOfferSelectAll =
      canSelectAllInCurrentView &&
      !isSelectAllActiveFromHook &&
      !isSearchActive &&
      !!selectAllTotalCount &&
      selectAllTotalCount > 1;

    const { showSelectionDropdown } = useMemo(
      () =>
        getSelectionActionBarItemCountState({
          hasOnlyCanvasesSelected,
          selectAll: isSelectAllActiveFromHook,
          selectedItemCount: selectedItems.length,
        }),
      [hasOnlyCanvasesSelected, isSelectAllActiveFromHook, selectedItems.length],
    );

    const showItemCountChevron = showSelectionDropdown && selectedItems.length > 1;

    const liveLayoutMeasurement = useMemo(
      () => ({
        canOfferSelectAll,
        canSelectAllInCurrentView,
        countLabel,
        hasScopeLabel,
        hasShowcaseShare,
        scopeLabel,
        selectAllLabel,
        showItemCountChevron,
        willOfferSelectAll,
      }),
      [
        canOfferSelectAll,
        canSelectAllInCurrentView,
        countLabel,
        hasScopeLabel,
        hasShowcaseShare,
        scopeLabel,
        selectAllLabel,
        showItemCountChevron,
        willOfferSelectAll,
      ],
    );

    const { value: layoutMeasurement } = useFrozenWhile(liveLayoutMeasurement, shouldFreezeViewModel);

    const { controlMeasureRefs, measureRef, tierFlags } = useSelectionBarMeasuredLayoutTier(layoutMeasurement);

    const { menuOptions, actions } = usePrivateSelectionBarActions(tierFlags);

    const showSelectAll = canSelectAllInCurrentView && canOfferSelectAll && !tierFlags.hideSelectAllButton;

    const liveViewModel = useMemo<AgentSelectionBarViewModel>(
      () => ({
        actions,
        compactOutlineCtAs: tierFlags.compactOutlineCtAs,
        countLabel,
        endCta,
        hasSelection: visibilityHasSelection,
        hidePrefixActions: tierFlags.hidePrefixActions,
        hideScopeLabel: tierFlags.hideScopeLabel,
        hideThumbStrip: tierFlags.hideThumbStrip,
        isSelectAllActive: isSelectAllActiveFromHook,
        menuOptions,
        scopeLabel,
        selectAllLabel,
        selectedItems,
        showIncludeNestedItems:
          canSelectAllInCurrentView &&
          canIncludeNestedItems &&
          (!tierFlags.hideIncludeNestedButton || isSelectAllActiveFromHook),
        showSelectAll,
        showSelectionDropdown,
      }),
      [
        actions,
        canIncludeNestedItems,
        canSelectAllInCurrentView,
        countLabel,
        endCta,
        menuOptions,
        scopeLabel,
        selectAllLabel,
        selectedItems,
        showSelectAll,
        showSelectionDropdown,
        tierFlags.compactOutlineCtAs,
        tierFlags.hideIncludeNestedButton,
        tierFlags.hidePrefixActions,
        tierFlags.hideScopeLabel,
        tierFlags.hideThumbStrip,
        visibilityHasSelection,
        isSelectAllActiveFromHook,
      ],
    );

    // Hold the last drawn state while the store is empty: during collapse, and during a replace-click
    // mousedown that has cleared `selectedItems` but not yet committed the new selection on mouseup.
    const { value: viewModel } = useFrozenWhile(liveViewModel, shouldFreezeViewModel);

    return (
      <AgentSelectionBar
        ref={measureRef}
        controlMeasureRefs={controlMeasureRefs}
        onClearAll={onClearAll}
        onIncludeNestedItems={enableIncludeNestedItems}
        onToggleSelectAll={toggleSelectAll}
        viewModel={viewModel}
      />
    );
  },
);

PrivateAgentSelectionBar.displayName = 'PrivateAgentSelectionBar';
